import React from "react";
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from "lucide-react";

/**
 * Coloured chip showing whether a prebuilt dashboard's required canonical
 * data models actually have rows in the caller's tenant.
 *
 * Backend payload (from GET /api/dashboards/templates):
 *   readiness: { status: "ready" | "partial" | "no_data" | "missing_binding",
 *                per_model: [{ model_id, row_count, ready }] }
 *
 * Status mapping
 *   ready          → ✅ green   "Ready"
 *   partial        → ⚠️ amber   "Partial"
 *   no_data        → ❌ red     "Connect data"
 *   missing_binding → ❔ grey    "Custom"   (no canonical models bound — usually a Builder draft)
 *
 * Tooltip lists each missing model so the user knows which connector to wire.
 */
export default function ReadinessBadge({ readiness, compact = false, "data-testid": testId }) {
    if (!readiness) return null;
    const status = readiness.status || "missing_binding";
    const perModel = Array.isArray(readiness.per_model) ? readiness.per_model : [];

    const config = {
        ready:    { Icon: CheckCircle2,  label: "Ready",         tone: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30" },
        partial:  { Icon: AlertTriangle, label: "Partial",       tone: "text-amber-600 bg-amber-500/10 border-amber-500/30" },
        no_data:  { Icon: XCircle,       label: "Connect data",  tone: "text-rose-600 bg-rose-500/10 border-rose-500/30" },
        missing_binding: { Icon: HelpCircle, label: "Custom",   tone: "text-muted-foreground bg-secondary/40 border-border" },
    }[status] || { Icon: HelpCircle, label: status, tone: "text-muted-foreground bg-secondary/40 border-border" };

    // Tooltip — list which models are missing for partial / no_data.
    const missing = perModel.filter((p) => !p.ready).map((p) => p.model_id);
    const tooltip = (() => {
        if (status === "ready") return `All ${perModel.length} required canonical model${perModel.length === 1 ? "" : "s"} have data in your tenant.`;
        if (status === "partial") return `Missing data: ${missing.join(", ")}. Connect a source that exposes ${missing.join(" or ")}.`;
        if (status === "no_data") return `No data yet for: ${perModel.map((p) => p.model_id).join(", ")}. Connect a data source to enable this prebuilt.`;
        return "This template was not bound to any canonical data model — it may be a custom Builder draft.";
    })();

    const { Icon, label, tone } = config;
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${tone}`}
            title={tooltip}
            data-testid={testId || `readiness-${status}`}>
            <Icon className="h-3 w-3" />
            {!compact && label}
        </span>
    );
}
